/**
 * Map utility functions for panning, zooming, and fitting bounds
 */

export interface Coordinate {
  latitude: number;
  longitude: number;
}

export interface MapPanOptions {
  padding?: number; // Padding in pixels around the bounds
  maxZoom?: number; // Maximum zoom level to prevent zooming in too close
  minZoom?: number; // Minimum zoom level to prevent zooming out too far
}

const cameraAnimations = new WeakMap<google.maps.Map, number>();
const ZOOM_EPSILON = 1e-12;
const FLY_RHO = Math.SQRT2;
const FLY_RHO2 = FLY_RHO * FLY_RHO;
const FLY_RHO4 = FLY_RHO2 * FLY_RHO2;

const cancelCameraAnimation = (map: google.maps.Map) => {
  const existingAnimation = cameraAnimations.get(map);
  if (existingAnimation) {
    cancelAnimationFrame(existingAnimation);
    cameraAnimations.delete(map);
  }
};

const easeInOutCubic = (t: number): number =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

const latLngToWorld = (lat: number, lng: number) => {
  const clampedLat = Math.min(Math.max(lat, -85.05112878), 85.05112878);
  const siny = Math.sin((clampedLat * Math.PI) / 180);
  return {
    x: (lng + 180) / 360,
    y: 0.5 - Math.log((1 + siny) / (1 - siny)) / (4 * Math.PI),
  };
};

const worldToLatLng = (x: number, y: number) => {
  const n = Math.PI * (1 - 2 * y);
  return {
    lat: (180 / Math.PI) * Math.atan(Math.sinh(n)),
    lng: x * 360 - 180,
  };
};

const viewportWidthAtZoom = (map: google.maps.Map, zoom: number) => {
  const widthPx = map.getDiv()?.clientWidth || 256;
  return widthPx / (256 * Math.pow(2, zoom));
};

const zoomFromViewportWidth = (map: google.maps.Map, width: number) => {
  const widthPx = map.getDiv()?.clientWidth || 256;
  return Math.log2(widthPx / (256 * width));
};

/**
 * Van Wijk / Nuij zoom interpolation so pan and zoom travel as one camera path
 * instead of zooming first and panning the leftover distance at the end.
 */
const interpolateFly = (
  start: { x: number; y: number; w: number },
  end: { x: number; y: number; w: number }
) => {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const d2 = dx * dx + dy * dy;

  if (d2 < ZOOM_EPSILON) {
    const S = Math.log(end.w / start.w) / FLY_RHO;
    return (t: number) => ({
      x: start.x + t * dx,
      y: start.y + t * dy,
      w: start.w * Math.exp(FLY_RHO * t * S),
    });
  }

  const d1 = Math.sqrt(d2);
  const b0 =
    (end.w * end.w - start.w * start.w + FLY_RHO4 * d2) /
    (2 * start.w * FLY_RHO2 * d1);
  const b1 =
    (end.w * end.w - start.w * start.w - FLY_RHO4 * d2) /
    (2 * end.w * FLY_RHO2 * d1);
  const r0 = Math.asinh(-b0);
  const r1 = Math.asinh(-b1);
  const S = (r1 - r0) / FLY_RHO;

  return (t: number) => {
    const s = t * S;
    const coshR0 = Math.cosh(r0);
    const u =
      ((start.w / (FLY_RHO2 * d1)) *
        (coshR0 * Math.tanh(FLY_RHO * s + r0) - Math.sinh(r0)));
    return {
      x: start.x + u * dx,
      y: start.y + u * dy,
      w: (start.w * coshR0) / Math.cosh(FLY_RHO * s + r0),
    };
  };
};

/**
 * Pans the map to show a region containing all the provided coordinates
 * @param map - Google Maps instance
 * @param coordinates - Array of coordinates to fit in the view
 * @param options - Optional configuration for panning behavior
 */
export const panToRegion = (
  map: google.maps.Map,
  coordinates: Coordinate[],
  options: MapPanOptions = {}
): void => {
  if (!map || coordinates.length === 0) return;

  cancelCameraAnimation(map);

  const { padding = 50, maxZoom = 18, minZoom = 1 } = options;

  // Create bounds object
  const bounds = new google.maps.LatLngBounds();

  // Add all coordinates to bounds
  coordinates.forEach((coord) => {
    bounds.extend({
      lat: coord.latitude,
      lng: coord.longitude,
    });
  });

  // Fit bounds with padding
  map.fitBounds(bounds, padding);

  // Apply zoom constraints after fitting bounds
  const currentZoom = map.getZoom();
  if (currentZoom !== undefined) {
    if (currentZoom > maxZoom) {
      map.setZoom(maxZoom);
    } else if (currentZoom < minZoom) {
      map.setZoom(minZoom);
    }
  }
};

/**
 * Pans the map to center on a single coordinate with a smooth camera transition.
 * @param map - Google Maps instance
 * @param coordinate - Coordinate to center on
 * @param zoom - Optional zoom level (defaults to 15)
 * @param durationMs - Animation duration in milliseconds
 */
export const panToCoordinate = (
  map: google.maps.Map,
  coordinate: Coordinate,
  zoom: number = 15,
  durationMs: number = 1200
): void => {
  if (!map) return;

  cancelCameraAnimation(map);

  const startCenter = map.getCenter();
  const startZoom = map.getZoom() ?? zoom;
  const endLat = coordinate.latitude;
  const endLng = coordinate.longitude;

  if (!startCenter || durationMs <= 0) {
    map.moveCamera({
      center: { lat: endLat, lng: endLng },
      zoom,
    });
    return;
  }

  const startLat = startCenter.lat();
  const startLng = startCenter.lng();
  const startWorld = latLngToWorld(startLat, startLng);
  const endWorld = latLngToWorld(endLat, endLng);
  const flyAt = interpolateFly(
    { ...startWorld, w: viewportWidthAtZoom(map, startZoom) },
    { ...endWorld, w: viewportWidthAtZoom(map, zoom) }
  );
  const startTime = performance.now();

  const step = (now: number) => {
    const progress = Math.min((now - startTime) / durationMs, 1);

    if (progress >= 1) {
      map.moveCamera({
        center: { lat: endLat, lng: endLng },
        zoom,
      });
      cameraAnimations.delete(map);
      return;
    }

    const camera = flyAt(easeInOutCubic(progress));
    const center = worldToLatLng(camera.x, camera.y);

    map.moveCamera({
      center,
      zoom: zoomFromViewportWidth(map, camera.w),
    });

    cameraAnimations.set(map, requestAnimationFrame(step));
  };

  cameraAnimations.set(map, requestAnimationFrame(step));
};

const latRad = (lat: number) => {
  const sin = Math.sin((lat * Math.PI) / 180);
  const radX2 = Math.log((1 + sin) / (1 - sin)) / 2;
  return Math.max(Math.min(radX2, Math.PI), -Math.PI) / 2;
};

const zoomForFraction = (
  mapPixels: number,
  worldPixels: number,
  fraction: number
) => {
  if (fraction <= 0) {
    return 21;
  }
  return Math.log2(mapPixels / worldPixels / fraction);
};

const getBoundsZoomLevel = (
  map: google.maps.Map,
  coordinates: Coordinate[],
  padding: number
) => {
  if (coordinates.length <= 1) {
    return 8;
  }

  const bounds = calculateBounds(coordinates);
  const mapDiv = map.getDiv();
  const width = Math.max((mapDiv?.clientWidth ?? 256) - padding * 2, 64);
  const height = Math.max((mapDiv?.clientHeight ?? 256) - padding * 2, 64);

  const latFraction = (latRad(bounds.north) - latRad(bounds.south)) / Math.PI;
  const lngDiff = bounds.east - bounds.west;
  const lngFraction = ((lngDiff < 0 ? lngDiff + 360 : lngDiff) || 360) / 360;

  return Math.min(
    zoomForFraction(height, 256, latFraction),
    zoomForFraction(width, 256, lngFraction)
  );
};

/**
 * Smoothly flies the camera to a view that contains all coordinates.
 */
export const flyToCoordinates = (
  map: google.maps.Map,
  coordinates: Coordinate[],
  options: MapPanOptions & {
    fallback?: { center: Coordinate; zoom: number };
  } = {}
): void => {
  if (!map) return;

  const { padding = 80, maxZoom = 8, minZoom = 3, fallback } = options;

  if (coordinates.length === 0) {
    if (fallback) {
      panToCoordinate(map, fallback.center, fallback.zoom);
    }
    return;
  }

  const center = calculateCenter(coordinates);
  const zoom = Math.min(
    Math.max(getBoundsZoomLevel(map, coordinates, padding), minZoom),
    maxZoom
  );
  panToCoordinate(map, center, zoom);
};

/**
 * Pans the map to show a region with a specific center and radius
 * @param map - Google Maps instance
 * @param center - Center coordinate
 * @param radiusKm - Radius in kilometers
 * @param options - Optional configuration
 */
export const panToRegionWithRadius = (
  map: google.maps.Map,
  center: Coordinate,
  radiusKm: number,
  options: MapPanOptions = {}
): void => {
  if (!map) return;

  const { padding = 50, maxZoom = 18, minZoom = 1 } = options;

  // Calculate bounds based on radius
  const earthRadius = 6371; // Earth's radius in kilometers
  const latDelta = (radiusKm / earthRadius) * (180 / Math.PI);
  const lngDelta =
    ((radiusKm / earthRadius) * (180 / Math.PI)) /
    Math.cos((center.latitude * Math.PI) / 180);

  const bounds = new google.maps.LatLngBounds();
  bounds.extend({
    lat: center.latitude + latDelta,
    lng: center.longitude + lngDelta,
  });
  bounds.extend({
    lat: center.latitude - latDelta,
    lng: center.longitude - lngDelta,
  });

  map.fitBounds(bounds, padding);

  // Apply zoom constraints
  const currentZoom = map.getZoom();
  if (currentZoom !== undefined) {
    if (currentZoom > maxZoom) {
      map.setZoom(maxZoom);
    } else if (currentZoom < minZoom) {
      map.setZoom(minZoom);
    }
  }
};

/**
 * Calculates the center point of multiple coordinates
 * @param coordinates - Array of coordinates
 * @returns Center coordinate
 */
export const calculateCenter = (coordinates: Coordinate[]): Coordinate => {
  if (coordinates.length === 0) {
    return { latitude: 0, longitude: 0 };
  }

  if (coordinates.length === 1) {
    return coordinates[0];
  }

  let latSum = 0;
  let lngSum = 0;

  coordinates.forEach((coord) => {
    latSum += coord.latitude;
    lngSum += coord.longitude;
  });

  return {
    latitude: latSum / coordinates.length,
    longitude: lngSum / coordinates.length,
  };
};

/**
 * Calculates the bounding box for a set of coordinates
 * @param coordinates - Array of coordinates
 * @returns Bounding box with min/max lat/lng
 */
export const calculateBounds = (coordinates: Coordinate[]) => {
  if (coordinates.length === 0) {
    return {
      north: 0,
      south: 0,
      east: 0,
      west: 0,
    };
  }

  let north = coordinates[0].latitude;
  let south = coordinates[0].latitude;
  let east = coordinates[0].longitude;
  let west = coordinates[0].longitude;

  coordinates.forEach((coord) => {
    north = Math.max(north, coord.latitude);
    south = Math.min(south, coord.latitude);
    east = Math.max(east, coord.longitude);
    west = Math.min(west, coord.longitude);
  });

  return { north, south, east, west };
};
